import { computed, Injectable, signal } from '@angular/core';

/**
 * Esito dell'inizializzazione Keycloak, per interrompere il loop di login.
 *
 * Scenario visto in dev il 21/09/2026: il client del realm richiede un segreto
 * che una SPA non ha, quindi lo scambio del `code` al ritorno da Keycloak
 * fallisce. keycloak-angular ingoia l'errore e avvia l'app come anonima, il
 * guard della rotta protetta rilancia `login()`, Keycloak ha già il cookie di
 * sessione e rimanda subito indietro con un nuovo `code`: da capo, all'infinito.
 *
 * Qui si ricorda che il ritorno da Keycloak non ha prodotto una sessione. I
 * guard, in questo stato, non avviano un nuovo login automatico ma portano su
 * una pagina pubblica e fanno comparire l'avviso. Un login richiesto
 * esplicitamente dall'utente azzera lo stato: quel tentativo è legittimo, e se
 * fallisce di nuovo si torna qui senza mai entrare in loop.
 *
 * Solo in memoria: al ricaricamento successivo l'init viene ritentato.
 */
@Injectable({
  providedIn: 'root',
})
export class KeycloakInitStateService {
  private readonly loginFailedSignal = signal(false);
  private readonly noticeVisibleSignal = signal(false);

  /** Il ritorno da Keycloak non ha aperto una sessione. */
  readonly loginFailed = computed(() => this.loginFailedSignal());

  /** L'avviso nel layout è da mostrare. */
  readonly noticeVisible = computed(() => this.noticeVisibleSignal());

  markLoginFailed(): void {
    this.loginFailedSignal.set(true);
    this.noticeVisibleSignal.set(true);
  }

  /**
   * Un guard ha bloccato l'ingresso in una rotta protetta: l'avviso torna
   * visibile anche se l'utente lo aveva chiuso, altrimenti il rimbalzo sulla
   * pagina pubblica resterebbe senza spiegazione.
   */
  showNotice(): void {
    if (this.loginFailedSignal()) {
      this.noticeVisibleSignal.set(true);
    }
  }

  dismissNotice(): void {
    this.noticeVisibleSignal.set(false);
  }

  /** Nuovo tentativo esplicito di login: si riparte puliti. */
  reset(): void {
    this.loginFailedSignal.set(false);
    this.noticeVisibleSignal.set(false);
  }
}
